import { dpopKeyManager } from './dpopKeyManager';
import { base64urlEncode, base64urlEncodeJson, createAth, normalizeHtu } from './dpopUtils';
import { DpopKeyMissingError, DpopProofOptions, DpopProofPayload } from './types';

//Create a DPoP proof JWT per RFC 9449 §4.2
export const createDpopProof = async (options: DpopProofOptions): Promise<string> => {
  const state = dpopKeyManager.get();
  if (!state) {
    throw new DpopKeyMissingError();
  }

  const htu = normalizeHtu(options.url);
  const htm = options.method.toUpperCase();

  const proofPayload: DpopProofPayload = {
    jti: crypto.randomUUID(),
    htm,
    htu,
    iat: Math.floor(Date.now() / 1000),
  };

  if (options.accessToken !== undefined) {
    proofPayload.ath = await createAth(options.accessToken);
  }

  const header = {
    typ: 'dpop+jwt',
    alg: 'ES256',
    jwk: state.publicJwk,
  };

  const headerB64 = base64urlEncodeJson(header);
  const payloadB64 = base64urlEncodeJson(proofPayload);
  const signingInput = `${headerB64}.${payloadB64}`;

  // Web Crypto ECDSA returns IEEE P1363 format (r||s, 64 bytes for P-256)
  // that is exactly what JWT requires, no DER conversion needed.
  const signingBytes = new TextEncoder().encode(signingInput);
  const signatureBuffer = await crypto.subtle.sign(
    { name: 'ECDSA', hash: { name: 'SHA-256' } },
    state.privateKey,
    signingBytes,
  );

  const signatureB64 = base64urlEncode(new Uint8Array(signatureBuffer));
  return `${signingInput}.${signatureB64}`;
};
